import { useState, useRef, useEffect } from 'react';
import { DayPicker, type DateRange } from 'react-day-picker';
import { CalendarDays } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ymdToLocalDate, localDateToYMD } from '@/lib/calendar';
import './date-picker.css';

// ---------------------------------------------------------------------------
// DatePicker — single date
// ---------------------------------------------------------------------------

export interface DatePickerProps {
  value?: string; // YYYY-MM-DD or undefined
  onChange: (d?: string) => void;
  placeholder?: string;
}

export function DatePicker({ value, onChange, placeholder = 'Pick a date' }: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click (same pattern as MultiSelectFilter)
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  const selected = value ? ymdToLocalDate(value) : undefined;

  return (
    <div className="relative" ref={ref}>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <CalendarDays className="h-4 w-4" aria-hidden="true" />
        {value ?? placeholder}
      </Button>

      {open && (
        <div
          role="dialog"
          aria-label="Date picker"
          className="absolute left-0 z-40 mt-1 rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-3 shadow-lg"
        >
          <DayPicker
            mode="single"
            selected={selected}
            defaultMonth={selected}
            onSelect={(d: Date | undefined) => {
              onChange(d ? localDateToYMD(d) : undefined);
              setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DateRangePicker — date range
// ---------------------------------------------------------------------------

export interface DateRangeValue {
  from?: string;
  to?: string;
}

export interface DateRangePickerProps {
  value: DateRangeValue;
  onChange: (r: DateRangeValue) => void;
  placeholder?: string;
  /** Earliest selectable day, YYYY-MM-DD. Earlier days render disabled. */
  disabledBefore?: string;
  /** Months shown side by side (default 1). Two make a month-long range one drag. */
  months?: number;
}

export function DateRangePicker({
  value,
  onChange,
  placeholder = 'Pick dates',
  disabledBefore,
  months = 1,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  const selected: DateRange = {
    from: value.from ? ymdToLocalDate(value.from) : undefined,
    to: value.to ? ymdToLocalDate(value.to) : undefined,
  };

  const label =
    value.from && value.to
      ? `${value.from} → ${value.to}`
      : value.from
        ? `${value.from} → …`
        : placeholder;

  return (
    <div className="relative" ref={ref}>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <CalendarDays className="h-4 w-4" aria-hidden="true" />
        {label}
      </Button>

      {open && (
        <div
          role="dialog"
          aria-label="Date range picker"
          className="absolute left-0 z-40 mt-1 rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card p-3 shadow-lg"
        >
          <DayPicker
            mode="range"
            selected={selected}
            defaultMonth={selected.from}
            numberOfMonths={months}
            disabled={disabledBefore ? { before: ymdToLocalDate(disabledBefore) } : undefined}
            onSelect={(r: DateRange | undefined) => {
              const from = r?.from ? localDateToYMD(r.from) : undefined;
              const to = r?.to ? localDateToYMD(r.to) : undefined;
              onChange({ from, to });
              // A range spanning at least one night means the operator is done
              // here. Staying open leaves a two-month calendar covering the rest
              // of the form — inside the booking drawer it sits directly on top
              // of the guest fields and eats their clicks. The first click of a
              // range reports from === to, which is not done yet.
              if (from && to && from !== to) setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}
